import { useState } from "react";
import appearanceLogo from "../../assets/appearance-logo.png";
import { api } from "../../state/api";
import {
  BrandMode,
  type AppConfigurationResponse,
  type AppResponse,
} from "../../state/api.types";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { FormCard } from "../shared/FormCard";
import { BrandModeLockedNotice } from "./BrandModeLockedNotice";
import { ImageDrop } from "./ImageDrop";
import { LogoPreview } from "./LogoPreview";

const LOGO_SUBTEXT =
  "Upload a wide transparent PNG to show up in emails, embedded flows, and more.";

type AppearanceLogoCardProps = {
  app: AppResponse;
  configuration: AppConfigurationResponse;
};

export const AppearanceLogoCard = (props: AppearanceLogoCardProps) => {
  const [updateConfigurationLogo, updateConfigurationLogoResult] =
    useMutationWithToast(api.useUpdateConfigurationLogoMutation(), {
      successMessage: "The app's logo has been updated",
    });

  const [file, setFile] = useState<File | null>(null);
  const [src, setSrc] = useState(props.configuration.logo);

  if (props.configuration.brand_mode === BrandMode.Subtle) {
    return (
      <FormCard name="Logo" subtext={LOGO_SUBTEXT}>
        <BrandModeLockedNotice />
      </FormCard>
    );
  }

  return (
    <FormCard
      name="Logo"
      subtext={LOGO_SUBTEXT}
      textCancel="Reset"
      buttonText="Save image"
      isDisabled={!file}
      isLoading={updateConfigurationLogoResult.isLoading}
      onCancel={() => {
        setFile(null);
        setSrc(props.configuration.logo);
      }}
      onButtonClick={() => {
        if (file) {
          updateConfigurationLogo({ appUUID: props.app.uuid, logo: file });
        }
      }}
      data-ph-capture-attribute-action="update-logo"
    >
      <div className="grid grid-cols-2 gap-3xl">
        <ImageDrop
          className="size-full"
          height={60}
          width={240}
          src={src}
          isDisabled={updateConfigurationLogoResult.isLoading}
          onFile={(f) => {
            setFile(f);
            setSrc(URL.createObjectURL(f));
          }}
        />
        <LogoPreview className="size-full" src={src ?? appearanceLogo} />
      </div>
    </FormCard>
  );
};
